/**
 * A HubSpot form, embedded.
 *
 * HubSpot's script scans the page for its target div and fills it in. That
 * ordering is the whole difficulty in a single-page app: the div appears and
 * disappears as screens change, while a script tag, once executed, is not run
 * again by the browser just because something new was rendered.
 *
 * So the script is mounted with the screen and removed with it. Re-adding a
 * script element re-executes it - from cache, so at no network cost - which
 * makes opening a form page a second time build the form again, correctly. The
 * container is emptied on the way out, because what HubSpot leaves behind is a
 * rendered form, and React does not own those nodes and will not clear them.
 *
 * Shared by both form pages rather than written twice: the subtlety above is
 * worth getting right once.
 */
import { useEffect, useRef } from "react";

import { HUBSPOT } from "../demo";

export function HubspotForm({ formId, title }: { formId: string; title: string }) {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = host.current;
    if (!container) return;

    const frame = document.createElement("div");
    frame.className = "hs-form-frame";
    frame.dataset.region = HUBSPOT.region;
    frame.dataset.formId = formId;
    frame.dataset.portalId = HUBSPOT.portalId;
    container.appendChild(frame);

    const script = document.createElement("script");
    script.src = HUBSPOT.script;
    script.defer = true;
    container.appendChild(script);

    return () => {
      // Everything this effect put on the page, including whatever the script
      // rendered into the frame. Leaving it would give a second visit two forms.
      container.replaceChildren();
    };
    // Keyed on the form: switching between the two pages has to rebuild, not
    // reuse - they are different forms in the same shape of container.
  }, [formId]);

  return <div className="contact__form" ref={host} aria-label={title} />;
}
